import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  StarIcon,
  MapPinIcon,
  ClockIcon,
  CalendarDaysIcon,
  UserIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';

interface Teacher {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  instruments: string[];
  specialties?: string[];
  rating: number;
  totalReviews: number;
  hourlyRate: number;
  location: string;
  distance?: string;
  profilePicture?: string;
  bio: string;
  experience: string;
  availability?: string[];
  lessonFormats?: string[];
}

interface StudentPreferences {
  instrument: string;
  genres: string[];
  skillLevel: string;
  lessonLocation: string[];
  age: string;
  learnerType: 'learner' | 'guardian';
  learnerFirstName?: string;
}

interface TimeSlot {
  day: string;
  time: string;
  available: boolean;
}

interface BookingData {
  lessonType: string;
  selectedDate: string;
  selectedTime: string;
  duration: number; // in minutes
  totalPrice: number;
  specialRequests?: string;
}

// Default lesson formats if not provided
const DEFAULT_LESSON_FORMATS = ['Online', 'On-site (teacher\'s studio)', 'In-home (student\'s home)'];

// Mock available time slots for the next week
const generateTimeSlots = (): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const times = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '7:00 PM'];
  
  days.forEach(day => {
    times.forEach(time => {
      slots.push({
        day,
        time,
        available: Math.random() > 0.3 // 70% of slots are available
      });
    });
  });
  
  return slots;
};

const BookLessonPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { teacher, studentPreferences } = location.state || {};
  const [currentStep, setCurrentStep] = useState(1);
  const [timeSlots] = useState<TimeSlot[]>(generateTimeSlots());
  const [bookingData, setBookingData] = useState<Partial<BookingData>>({
    duration: 30 // Default to 30 minutes
  });

  // Log booking page access
  console.log('📚 Book lesson page loaded for teacher:', teacher);
  console.log('📚 Student preferences:', studentPreferences);

  if (!teacher) {
    console.warn('❌ No teacher data found, redirecting to browse teachers');
    navigate('/browse-teachers');
    return null;
  }

  // Ensure teacher has required properties with fallbacks
  const teacherData = {
    ...teacher,
    name: teacher.name || `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() || 'Unknown Teacher',
    hourlyRate: teacher.hourlyRate || 50,
    lessonFormats: teacher.lessonFormats && teacher.lessonFormats.length > 0 ? teacher.lessonFormats : DEFAULT_LESSON_FORMATS,
    rating: teacher.rating || 0,
    totalReviews: teacher.totalReviews || 0
  };

  const isGuardian = studentPreferences?.learnerType === 'guardian';
  const learnerName = studentPreferences?.learnerFirstName || 'you';

  const handleLessonTypeSelect = (lessonType: string) => {
    setBookingData(prev => ({ ...prev, lessonType }));
    console.log('📚 Lesson type selected:', lessonType);
  };

  const handleTimeSlotSelect = (day: string, time: string) => {
    setBookingData(prev => ({ 
      ...prev, 
      selectedDate: day, 
      selectedTime: time 
    }));
    console.log('📅 Time slot selected:', day, time);
  };

  const handleDurationChange = (duration: number) => {
    const basePrice = teacherData.hourlyRate;
    const totalPrice = duration === 30 ? basePrice / 2 : basePrice;
    
    setBookingData(prev => ({ 
      ...prev, 
      duration,
      totalPrice 
    }));
    console.log('⏱️ Duration changed to:', duration, 'Price:', totalPrice);
  };

  const handleNext = () => {
    if (currentStep === 1 && !bookingData.lessonType) {
      alert('Please select a lesson type.');
      return;
    }
    if (currentStep === 2 && (!bookingData.selectedDate || !bookingData.selectedTime)) {
      alert('Please select a date and time.');
      return;
    }
    
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleBookingSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate('/teacher-detail', { state: { teacher, studentPreferences } });
    }
  };

  const handleBookingSubmit = () => {
    console.log('📚 Submitting booking:', bookingData);
    // TODO: Submit to backend API
    
    // Navigate to confirmation page
    navigate('/booking-confirmation', {
      state: {
        teacher: teacherData,
        studentPreferences,
        bookingData
      }
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Lesson Type Selection
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Choose Lesson Type</h3>
              <p className="text-gray-600 mb-6">
                Select how {isGuardian ? learnerName : 'you'} would like to take lessons with {teacherData.name}.
              </p>
            </div>

            <div className="space-y-4">
              {teacherData.lessonFormats.map((format: string) => (
                <div
                  key={format}
                  onClick={() => handleLessonTypeSelect(format)}
                  className={`cursor-pointer p-6 border-2 rounded-lg transition-all ${
                    bookingData.lessonType === format
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        bookingData.lessonType === format
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`} />
                      <div>
                        <h4 className="font-semibold text-gray-900">{format}</h4>
                        <p className="text-sm text-gray-600">
                          {format === 'Online' && 'Learn from anywhere with video calls'}
                          {format === 'On-site (teacher\'s studio)' && 'Visit the teacher\'s dedicated music studio'}
                          {format === 'In-home (student\'s home)' && 'Teacher comes to your location'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        ${(teacherData.hourlyRate / 2).toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600">per 30-min lesson</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 2: // Time Selection
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Select Date & Time</h3>
              <p className="text-gray-600 mb-6">
                Choose an available time slot for your {bookingData.lessonType?.toLowerCase()} lesson.
              </p>
            </div>

            {/* Duration Selection */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3">Lesson Duration</h4>
              <div className="flex space-x-4">
                <button
                  onClick={() => handleDurationChange(30)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    bookingData.duration === 30
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  30 minutes - ${(teacherData.hourlyRate / 2).toFixed(2)}
                </button>
                <button
                  onClick={() => handleDurationChange(60)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    bookingData.duration === 60
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  60 minutes - ${teacherData.hourlyRate.toFixed(2)}
                </button>
              </div>
            </div>

            {/* Time Slots Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                <div key={day} className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">{day}</h4>
                  <div className="space-y-2">
                    {timeSlots
                      .filter(slot => slot.day === day && slot.available)
                      .slice(0, 4) // Show first 4 available slots
                      .map((slot, index) => (
                      <button
                        key={index}
                        onClick={() => handleTimeSlotSelect(slot.day, slot.time)}
                        className={`w-full text-left px-3 py-2 text-sm rounded border transition-colors ${
                          bookingData.selectedDate === slot.day && bookingData.selectedTime === slot.time
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 3: // Confirmation
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Request a Lesson</h3>
              <p className="text-gray-600 mb-6">
                Please review your lesson details before sending your request to the teacher.
              </p>
            </div>

            {/* Booking Summary */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Teacher:</span>
                <span className="font-medium">{teacherData.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Lesson Type:</span>
                <span className="font-medium">{bookingData.lessonType}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Date & Time:</span>
                <span className="font-medium">{bookingData.selectedDate}, {bookingData.selectedTime}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Duration:</span>
                <span className="font-medium">{bookingData.duration} minutes</span>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Total:</span>
                  <span>${(bookingData.duration === 30 ? teacherData.hourlyRate / 2 : teacherData.hourlyRate).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Special Requests */}
            <div>
              <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-2">
                Special Requests (Optional)
              </label>
              <textarea
                id="specialRequests"
                rows={3}
                value={bookingData.specialRequests || ''}
                onChange={(e) => setBookingData(prev => ({ ...prev, specialRequests: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any specific topics you'd like to focus on or questions for the teacher..."
              />
            </div>

            {/* Important Notes */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Important Notes:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• You can reschedule or cancel up to 24 hours before the lesson</li>
                <li>• Payment will be processed after the lesson is completed</li>
                <li>• The teacher will contact you with any additional details</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gray-50">
        
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
              >
                <ChevronLeftIcon className="w-5 h-5" />
                <span>Back</span>
              </button>
              
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900">Book a Lesson</h1>
                <div className="flex items-center space-x-2 mt-1">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step <= currentStep 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {step}
                      </div>
                      {step < 3 && (
                        <div className={`w-12 h-1 ${
                          step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="w-16" /> {/* Spacer for centering */}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Teacher Info Card */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <UserIcon className="w-8 h-8 text-gray-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">{teacherData.name}</h2>
                <div className="flex items-center space-x-4 mt-2">
                  {renderStars(teacherData.rating)}
                  <span className="text-gray-600">({teacherData.totalReviews} reviews)</span>
                  <span className="text-gray-600">•</span>
                  <span className="text-gray-600">{teacherData.experience}</span>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <MapPinIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{teacherData.distance}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-sm border p-8">
            {renderStepContent()}
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <button
                onClick={handleBack}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {currentStep === 1 ? 'Back to Teacher' : 'Previous'}
              </button>
              
              <button
                onClick={handleNext}
                className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {currentStep === 3 ? 'Request Lesson' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default BookLessonPage; 